import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import PageHeader from './ui/PageHeader';
import {
  AlertTriangle, Bell, Calendar, Award, Cloud, Sun, Wind, Thermometer,
  Users, MapPin, Phone, Mail, Shield, Clock, CheckCircle, XCircle,
  Plus, AlertCircle, FileText, Loader2, ChevronRight, RefreshCw,
  Siren, Navigation, UserCheck, Droplets, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SafetyOperationsCenter = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('emergency');
  const [loading, setLoading] = useState(false);

  // Emergency State
  const [emergencyAlerts, setEmergencyAlerts] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [musterPoints, setMusterPoints] = useState([]);
  const [showPanicModal, setShowPanicModal] = useState(false);
  const [panicMessage, setPanicMessage] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddMusterPoint, setShowAddMusterPoint] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    role: '',
    phone: '',
    email: '',
    is_primary: false,
    available_24_7: true
  });
  const [newMusterPoint, setNewMusterPoint] = useState({
    name: '',
    location: '',
    capacity: 50,
    has_first_aid: false,
    has_communication: true
  });
  // Compliance State
  const [complianceItems, setComplianceItems] = useState([]);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [showAddCompliance, setShowAddCompliance] = useState(false);
  const [newCompliance, setNewCompliance] = useState({
    title: '',
    description: '',
    item_type: 'osha_inspection',
    due_date: '',
    priority: 'medium',
    recurring: false,
    recurrence_interval_days: 365
  });

  // Certification State
  const [certifications, setCertifications] = useState([]);
  const [certSummary, setCertSummary] = useState(null);
  const [showAddCert, setShowAddCert] = useState(false);
  const [newCert, setNewCert] = useState({
    worker_name: '',
    worker_id: '',
    certification_type: 'osha_30',
    certification_name: '',
    issuing_authority: '',
    issue_date: '',
    expiry_date: ''
  });

  // Weather State
  const [weather, setWeather] = useState(null);
  const [workConditions, setWorkConditions] = useState(null);
  const [forecast, setForecast] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEmergencyData(),
        fetchComplianceData(),
        fetchCertifications(),
        fetchWeatherData()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmergencyData = async () => {
    try {
      const [alertsRes, contactsRes, musterRes] = await Promise.all([
        axios.get(`${API}/safety-ops/emergency/alerts?active_only=true`),
        axios.get(`${API}/safety-ops/emergency/contacts`),
        axios.get(`${API}/safety-ops/emergency/muster-points`)
      ]);
      setEmergencyAlerts(alertsRes.data.alerts || []);
      setEmergencyContacts(contactsRes.data.contacts || []);
      setMusterPoints(musterRes.data.muster_points || []);
    } catch (error) {
      console.error('Error fetching emergency data:', error);
    }
  };

  const fetchComplianceData = async () => {
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/safety-ops/compliance/items?upcoming_days=90`),
        axios.get(`${API}/safety-ops/compliance/summary`)
      ]);
      setComplianceItems(itemsRes.data.items || []);
      setComplianceSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    }
  };

  const fetchCertifications = async () => {
    try {
      const [certsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/safety-ops/certifications?expiring_within_days=90`),
        axios.get(`${API}/safety-ops/certifications/summary`)
      ]);
      setCertifications(certsRes.data.certifications || []);
      setCertSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching certifications:', error);
    }
  };

  const fetchWeatherData = async () => {
    try {
      const [weatherRes, conditionsRes, forecastRes] = await Promise.all([
        axios.get(`${API}/safety-ops/weather/current`),
        axios.get(`${API}/safety-ops/weather/work-conditions`),
        axios.get(`${API}/safety-ops/weather/forecast?days=5`)
      ]);
      setWeather(weatherRes.data);
      setWorkConditions(conditionsRes.data);
      setForecast(forecastRes.data.forecast || []);
    } catch (error) {
      console.error('Error fetching weather data:', error);
    }
  };

  const addEmergencyContact = async () => {
    if (!newContact.name || !newContact.phone || !newContact.role) {
      toast({
        title: "Validation Error",
        description: "Please fill in name, role, and phone number",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await axios.post(`${API}/safety-ops/emergency/contacts`, newContact);
      
      toast({ title: "Success", description: "Emergency contact added" });
      setShowAddContact(false);
      setNewContact({
        name: '',
        role: '',
        phone: '',
        email: '',
        is_primary: false,
        available_24_7: true
      });
      fetchEmergencyData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add contact",
        variant: "destructive"
      });
    }
  };

  const addMusterPoint = async () => {
    if (!newMusterPoint.name || !newMusterPoint.location) {
      toast({
        title: "Validation Error",
        description: "Please fill in name and location",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await axios.post(`${API}/safety-ops/emergency/muster-points`, newMusterPoint);
      
      toast({ title: "Success", description: "Muster point added" });
      setShowAddMusterPoint(false);
      setNewMusterPoint({
        name: '',
        location: '',
        capacity: 50,
        has_first_aid: false,
        has_communication: true
      });
      fetchEmergencyData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add muster point",
        variant: "destructive"
      });
    }
  };

  const triggerPanicButton = async () => {
    try {
      const response = await axios.post(`${API}/safety-ops/emergency/panic-button`, {
        worker_id: 'current-user',
        worker_name: 'Current User',
        message: panicMessage || 'Emergency assistance needed!'
      });
      
      toast({
        title: "🚨 PANIC ALERT SENT",
        description: "Help is on the way! Stay calm and stay safe.",
        variant: "destructive"
      });
      
      setShowPanicModal(false);
      setPanicMessage('');
      fetchEmergencyData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send panic alert",
        variant: "destructive"
      });
    }
  };

  const addComplianceItem = async () => {
    try {
      await axios.post(`${API}/safety-ops/compliance/items`, {
        ...newCompliance,
        id: undefined
      });
      
      toast({ title: "Success", description: "Compliance item added" });
      setShowAddCompliance(false);
      setNewCompliance({
        title: '',
        description: '',
        item_type: 'osha_inspection',
        due_date: '',
        priority: 'medium',
        recurring: false,
        recurrence_interval_days: 365
      });
      fetchComplianceData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add item", variant: "destructive" });
    }
  };

  const completeComplianceItem = async (itemId) => {
    try {
      await axios.put(`${API}/safety-ops/compliance/items/${itemId}/complete?completed_by=current-user`);
      toast({ title: "Success", description: "Item marked complete" });
      fetchComplianceData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to complete item", variant: "destructive" });
    }
  };

  const addCertification = async () => {
    try {
      await axios.post(`${API}/safety-ops/certifications`, {
        ...newCert,
        id: undefined
      });
      
      toast({ title: "Success", description: "Certification added" });
      setShowAddCert(false);
      setNewCert({
        worker_name: '',
        worker_id: '',
        certification_type: 'osha_30',
        certification_name: '',
        issuing_authority: '',
        issue_date: '',
        expiry_date: ''
      });
      fetchCertifications();
    } catch (error) {
      toast({ title: "Error", description: "Failed to add certification", variant: "destructive" });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'overdue': 'bg-red-100 text-red-800 border-red-300',
      'due_soon': 'bg-orange-100 text-orange-800 border-orange-300',
      'upcoming': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'scheduled': 'bg-green-100 text-green-800 border-green-300',
      'expired': 'bg-red-100 text-red-800 border-red-300',
      'expiring_soon': 'bg-orange-100 text-orange-800 border-orange-300',
      'active': 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getWorkConditionColor = (status) => {
    const colors = {
      'GOOD': 'bg-green-500',
      'FAIR': 'bg-yellow-500',
      'POOR': 'bg-orange-500',
      'HAZARDOUS': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  // Emergency Response Tab
  const renderEmergencyTab = () => (
    <div className="space-y-6">
      {/* Panic Button */}
      <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-red-800 flex items-center gap-2">
                <Siren className="w-6 h-6" />
                Emergency Panic Button
              </h3>
              <p className="text-red-700 mt-1">Press in case of immediate danger or emergency</p>
            </div>
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 text-white h-16 px-8 text-lg animate-pulse"
              onClick={() => setShowPanicModal(true)}
            >
              <AlertTriangle className="w-6 h-6 mr-2" />
              PANIC
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Panic Modal */}
      {showPanicModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 border-red-500 border-2">
            <CardHeader className="bg-red-600 text-white">
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                CONFIRM EMERGENCY ALERT
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <p className="text-gray-700">
                This will immediately notify all emergency contacts and safety personnel.
              </p>
              <div>
                <Label>Additional Details (optional)</Label>
                <Textarea
                  value={panicMessage}
                  onChange={(e) => setPanicMessage(e.target.value)}
                  placeholder="Describe the emergency..."
                  className="mt-2"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={triggerPanicButton}
                >
                  SEND ALERT
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPanicModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Active Emergencies */}
      {emergencyAlerts.length > 0 && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="text-red-700 flex items-center gap-2">
              <Bell className="w-5 h-5 animate-bounce" />
              Active Emergencies ({emergencyAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {emergencyAlerts.map((alert) => (
              <div key={alert.id} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-red-800">{alert.title}</h4>
                    <p className="text-sm text-red-700">{alert.description}</p>
                    <p className="text-xs text-red-600 mt-1">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {alert.location}
                    </p>
                  </div>
                  <Badge className="bg-red-600">{alert.status}</Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Emergency Contacts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Emergency Contacts
            </CardTitle>
            <Button size="sm" onClick={() => setShowAddContact(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Contact
            </Button>
          </CardHeader>
          <CardContent>
            {emergencyContacts.length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No contacts configured</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowAddContact(true)}
                >
                  Add First Contact
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {emergencyContacts.map((contact) => (
                  <div key={contact.id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{contact.name}</p>
                      <p className="text-sm text-gray-600">{contact.role}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Phone className="w-3 h-3 text-blue-600" />
                        <p className="text-sm text-blue-600">{contact.phone}</p>
                      </div>
                      {contact.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3 text-gray-500" />
                          <p className="text-sm text-gray-500">{contact.email}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {contact.is_primary && (
                        <Badge className="bg-blue-500">Primary</Badge>
                      )}
                      {contact.available_24_7 && (
                        <Badge variant="outline" className="text-xs">24/7</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Muster Points */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-green-600" />
              Muster Points
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowAddMusterPoint(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Point
            </Button>
          </CardHeader>
          <CardContent>
            {musterPoints.length === 0 ? (
              <div className="text-center py-6">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No muster points configured</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowAddMusterPoint(true)}
                >
                  Add First Muster Point
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {musterPoints.map((point) => (
                  <div key={point.id} className="p-3 bg-green-50 rounded-lg">
                    <p className="font-medium text-green-800">{point.name}</p>
                    <p className="text-sm text-green-700">{point.location}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">
                        Capacity: {point.capacity}
                      </Badge>
                      {point.has_first_aid && (
                        <Badge className="bg-red-100 text-red-700 text-xs">First Aid</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Compliance Calendar Tab
  const renderComplianceTab = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      {complianceSummary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-700">{complianceSummary.total_items}</p>
              <p className="text-sm text-blue-600">Total Items</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-700">{complianceSummary.overdue}</p>
              <p className="text-sm text-red-600">Overdue</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-orange-700">{complianceSummary.due_this_week}</p>
              <p className="text-sm text-orange-600">Due This Week</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-700">{complianceSummary.due_this_month}</p>
              <p className="text-sm text-yellow-600">Due This Month</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-700">{complianceSummary.compliance_rate}%</p>
              <p className="text-sm text-green-600">Compliance Rate</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Compliance Item */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Upcoming Compliance Items</h3>
        <Button onClick={() => setShowAddCompliance(!showAddCompliance)}>
          <Plus className="w-4 h-4 mr-1" />
          Add Item
        </Button>
      </div>

      {showAddCompliance && (
        <Card className="border-blue-200">
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={newCompliance.title}
                  onChange={(e) => setNewCompliance({...newCompliance, title: e.target.value})}
                  placeholder="e.g., Annual OSHA Inspection"
                />
              </div>
              <div>
                <Label>Type</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={newCompliance.item_type}
                  onChange={(e) => setNewCompliance({...newCompliance, item_type: e.target.value})}
                >
                  <option value="osha_inspection">OSHA Inspection</option>
                  <option value="permit_renewal">Permit Renewal</option>
                  <option value="equipment_inspection">Equipment Inspection</option>
                  <option value="training_due">Training Due</option>
                  <option value="safety_audit">Safety Audit</option>
                  <option value="fire_drill">Fire Drill</option>
                  <option value="emergency_drill">Emergency Drill</option>
                </select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={newCompliance.due_date}
                  onChange={(e) => setNewCompliance({...newCompliance, due_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={newCompliance.priority}
                  onChange={(e) => setNewCompliance({...newCompliance, priority: e.target.value})}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newCompliance.description}
                onChange={(e) => setNewCompliance({...newCompliance, description: e.target.value})}
                placeholder="Add details..."
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newCompliance.recurring}
                  onChange={(e) => setNewCompliance({...newCompliance, recurring: e.target.checked})}
                />
                Recurring
              </label>
              {newCompliance.recurring && (
                <div className="flex items-center gap-2">
                  <span className="text-sm">Every</span>
                  <Input
                    type="number"
                    className="w-20"
                    value={newCompliance.recurrence_interval_days}
                    onChange={(e) => setNewCompliance({...newCompliance, recurrence_interval_days: parseInt(e.target.value)})}
                  />
                  <span className="text-sm">days</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={addComplianceItem}>Save Item</Button>
              <Button variant="outline" onClick={() => setShowAddCompliance(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Compliance Items List */}
      <div className="space-y-3">
        {complianceItems.map((item) => (
          <Card key={item.id} className={`border-l-4 ${
            item.status === 'overdue' ? 'border-l-red-500' :
            item.status === 'due_soon' ? 'border-l-orange-500' :
            item.status === 'upcoming' ? 'border-l-yellow-500' : 'border-l-green-500'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{item.title}</h4>
                    <Badge className={getStatusColor(item.status)}>
                      {item.status === 'overdue' ? `${item.days_overdue} days overdue` :
                       item.days_until_due !== undefined ? `${item.days_until_due} days` : item.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                  <div className="flex gap-4 mt-2 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Due: {new Date(item.due_date).toLocaleDateString()}
                    </span>
                    <Badge variant="outline">{item.item_type.replace(/_/g, ' ')}</Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-600"
                  onClick={() => completeComplianceItem(item.id)}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Complete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {complianceItems.length === 0 && (
          <p className="text-center text-gray-500 py-8">No upcoming compliance items</p>
        )}
      </div>
    </div>
  );

  // Certifications Tab
  const renderCertificationsTab = () => (
    <div className="space-y-6">
      {/* Summary */}
      {certSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4 text-center">
              <Award className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-3xl font-bold text-green-700">{certSummary.active}</p>
              <p className="text-sm text-green-600">Active</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 mx-auto text-orange-600 mb-2" />
              <p className="text-3xl font-bold text-orange-700">{certSummary.expiring_soon}</p>
              <p className="text-sm text-orange-600">Expiring Soon</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="p-4 text-center">
              <XCircle className="w-8 h-8 mx-auto text-red-600 mb-2" />
              <p className="text-3xl font-bold text-red-700">{certSummary.expired}</p>
              <p className="text-sm text-red-600">Expired</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4 text-center">
              <Shield className="w-8 h-8 mx-auto text-blue-600 mb-2" />
              <p className="text-3xl font-bold text-blue-700">{certSummary.compliance_rate}%</p>
              <p className="text-sm text-blue-600">Compliance</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Certification */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Certifications Requiring Attention</h3>
        <Button onClick={() => setShowAddCert(!showAddCert)}>
          <Plus className="w-4 h-4 mr-1" />
          Add Certification
        </Button>
      </div>

      {showAddCert && (
        <Card className="border-purple-200">
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Worker Name</Label>
                <Input
                  value={newCert.worker_name}
                  onChange={(e) => setNewCert({...newCert, worker_name: e.target.value, worker_id: e.target.value.toLowerCase().replace(/\s/g, '-')})}
                  placeholder="John Smith"
                />
              </div>
              <div>
                <Label>Certification Type</Label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={newCert.certification_type}
                  onChange={(e) => setNewCert({...newCert, certification_type: e.target.value})}
                >
                  <option value="osha_10">OSHA 10-Hour</option>
                  <option value="osha_30">OSHA 30-Hour</option>
                  <option value="first_aid">First Aid</option>
                  <option value="cpr_aed">CPR/AED</option>
                  <option value="forklift">Forklift Operator</option>
                  <option value="crane">Crane Operator</option>
                  <option value="confined_space">Confined Space</option>
                  <option value="fall_protection">Fall Protection</option>
                  <option value="hazmat">HAZMAT</option>
                  <option value="electrical">Electrical Safety</option>
                </select>
              </div>
              <div>
                <Label>Certification Name</Label>
                <Input
                  value={newCert.certification_name}
                  onChange={(e) => setNewCert({...newCert, certification_name: e.target.value})}
                  placeholder="e.g., OSHA 30-Hour Construction"
                />
              </div>
              <div>
                <Label>Issuing Authority</Label>
                <Input
                  value={newCert.issuing_authority}
                  onChange={(e) => setNewCert({...newCert, issuing_authority: e.target.value})}
                  placeholder="e.g., OSHA, Red Cross"
                />
              </div>
              <div>
                <Label>Issue Date</Label>
                <Input
                  type="date"
                  value={newCert.issue_date}
                  onChange={(e) => setNewCert({...newCert, issue_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Expiry Date</Label>
                <Input
                  type="date"
                  value={newCert.expiry_date}
                  onChange={(e) => setNewCert({...newCert, expiry_date: e.target.value})}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={addCertification}>Save Certification</Button>
              <Button variant="outline" onClick={() => setShowAddCert(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certifications List */}
      <div className="space-y-3">
        {certifications.map((cert) => (
          <Card key={cert.id} className={`border-l-4 ${
            cert.status === 'expired' ? 'border-l-red-500' :
            cert.status === 'expiring_soon' ? 'border-l-orange-500' : 'border-l-green-500'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold">{cert.worker_name}</h4>
                    <Badge className={getStatusColor(cert.status)}>
                      {cert.status === 'expired' ? `Expired ${cert.days_expired} days ago` :
                       cert.status === 'expiring_soon' ? `Expires in ${cert.days_until_expiry} days` : 'Active'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700 mt-1">{cert.certification_name}</p>
                  <div className="flex gap-4 mt-2 text-sm text-gray-500">
                    <span>Issued by: {cert.issuing_authority}</span>
                    <span>Expires: {new Date(cert.expiry_date).toLocaleDateString()}</span>
                  </div>
                </div>
                <Badge variant="outline">{cert.certification_type.replace(/_/g, ' ').toUpperCase()}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
        {certifications.length === 0 && (
          <p className="text-center text-gray-500 py-8">No certifications requiring attention</p>
        )}
      </div>
    </div>
  );

  // Weather Tab
  const renderWeatherTab = () => (
    <div className="space-y-6">
      {/* Current Conditions */}
      {workConditions && (
        <Card className={`border-2 ${
          workConditions.status === 'GOOD' ? 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50' :
          workConditions.status === 'FAIR' ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50' :
          workConditions.status === 'POOR' ? 'border-orange-300 bg-gradient-to-r from-orange-50 to-red-50' :
          'border-red-300 bg-gradient-to-r from-red-50 to-rose-50'
        }`}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Sun className="w-6 h-6 text-yellow-500" />
                Current Work Conditions
              </span>
              <Badge className={`${getWorkConditionColor(workConditions.status)} text-white text-lg px-4 py-1`}>
                {workConditions.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <Thermometer className="w-8 h-8 mx-auto text-red-500 mb-2" />
                <p className="text-3xl font-bold">{weather?.temperature || '--'}°F</p>
                <p className="text-sm text-gray-600">Temperature</p>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <Droplets className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                <p className="text-3xl font-bold">{weather?.humidity || '--'}%</p>
                <p className="text-sm text-gray-600">Humidity</p>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <Wind className="w-8 h-8 mx-auto text-gray-500 mb-2" />
                <p className="text-3xl font-bold">{weather?.wind_speed || '--'}</p>
                <p className="text-sm text-gray-600">Wind (mph)</p>
              </div>
              <div className="text-center p-4 bg-white/50 rounded-lg">
                <Eye className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                <p className="text-3xl font-bold">{workConditions.score}</p>
                <p className="text-sm text-gray-600">Safety Score</p>
              </div>
            </div>

            {/* Issues & Protocols */}
            {workConditions.issues?.length > 0 && (
              <div className="mb-4 p-4 bg-red-100 rounded-lg">
                <h4 className="font-semibold text-red-800 mb-2">⚠️ Current Issues</h4>
                <ul className="list-disc list-inside text-red-700">
                  {workConditions.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              </div>
            )}

            {workConditions.required_protocols?.length > 0 && (
              <div className="mb-4 p-4 bg-orange-100 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">📋 Required Protocols</h4>
                <ul className="list-disc list-inside text-orange-700">
                  {workConditions.required_protocols.map((protocol, i) => (
                    <li key={i}>{protocol}</li>
                  ))}
                </ul>
              </div>
            )}

            {workConditions.recommendations?.length > 0 && (
              <div className="p-4 bg-blue-100 rounded-lg">
                <h4 className="font-semibold text-blue-800 mb-2">💡 Recommendations</h4>
                <ul className="list-disc list-inside text-blue-700">
                  {workConditions.recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 5-Day Forecast */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            5-Day Work Conditions Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {forecast.map((day, i) => (
              <div key={i} className={`p-4 rounded-lg text-center ${
                day.work_suitability === 'Good' ? 'bg-green-50 border border-green-200' :
                day.work_suitability === 'Fair' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-red-50 border border-red-200'
              }`}>
                <p className="font-semibold text-gray-800">
                  {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                </p>
                <p className="text-xs text-gray-500">{day.date}</p>
                <Cloud className="w-8 h-8 mx-auto my-2 text-gray-400" />
                <p className="text-lg font-bold">{day.high}° / {day.low}°</p>
                <p className="text-xs text-gray-600">{day.conditions}</p>
                <Badge className={`mt-2 ${
                  day.work_suitability === 'Good' ? 'bg-green-500' :
                  day.work_suitability === 'Fair' ? 'bg-yellow-500' : 'bg-red-500'
                } text-white`}>
                  {day.work_suitability}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Universal Page Header */}
      <PageHeader
        title="Safety Operations Center"
        subtitle="Emergency Response • Compliance • Certifications • Weather"
        icon={Shield}
        variant="blue"
        onRefresh={fetchAllData}
        loading={loading}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="emergency" className="flex items-center gap-2">
            <Siren className="w-4 h-4" />
            <span className="hidden sm:inline">Emergency</span>
          </TabsTrigger>
          <TabsTrigger value="compliance" className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Compliance</span>
          </TabsTrigger>
          <TabsTrigger value="certifications" className="flex items-center gap-2">
            <Award className="w-4 h-4" />
            <span className="hidden sm:inline">Certifications</span>
          </TabsTrigger>
          <TabsTrigger value="weather" className="flex items-center gap-2">
            <Cloud className="w-4 h-4" />
            <span className="hidden sm:inline">Weather</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="emergency" className="mt-6">
          {renderEmergencyTab()}
        </TabsContent>

        <TabsContent value="compliance" className="mt-6">
          {renderComplianceTab()}
        </TabsContent>

        <TabsContent value="certifications" className="mt-6">
          {renderCertificationsTab()}
        </TabsContent>

        <TabsContent value="weather" className="mt-6">
          {renderWeatherTab()}
        </TabsContent>
      </Tabs>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={fetchAllData} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh Data
        </Button>
      </div>

      {/* Add Emergency Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-blue-600" />
              Add Emergency Contact
            </DialogTitle>
            <DialogDescription>
              Add a new safety contact for emergency communications
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Contact Name *</Label>
                <Input
                  value={newContact.name}
                  onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                  placeholder="John Smith"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Role / Title *</Label>
                <Select
                  value={newContact.role}
                  onValueChange={(value) => setNewContact({ ...newContact, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Safety Manager">Safety Manager</SelectItem>
                    <SelectItem value="Safety Officer">Safety Officer</SelectItem>
                    <SelectItem value="Emergency Coordinator">Emergency Coordinator</SelectItem>
                    <SelectItem value="First Aid Responder">First Aid Responder</SelectItem>
                    <SelectItem value="Fire Warden">Fire Warden</SelectItem>
                    <SelectItem value="Site Supervisor">Site Supervisor</SelectItem>
                    <SelectItem value="HR Manager">HR Manager</SelectItem>
                    <SelectItem value="Facilities Manager">Facilities Manager</SelectItem>
                    <SelectItem value="Security Officer">Security Officer</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={newContact.phone}
                  onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                  placeholder="john@company.com"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Primary Contact</Label>
                <Switch
                  checked={newContact.is_primary}
                  onCheckedChange={(checked) => setNewContact({ ...newContact, is_primary: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Available 24/7</Label>
                <Switch
                  checked={newContact.available_24_7}
                  onCheckedChange={(checked) => setNewContact({ ...newContact, available_24_7: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContact(false)}>
              Cancel
            </Button>
            <Button onClick={addEmergencyContact}>
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Muster Point Dialog */}
      <Dialog open={showAddMusterPoint} onOpenChange={setShowAddMusterPoint}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-green-600" />
              Add Muster Point
            </DialogTitle>
            <DialogDescription>
              Add a new emergency assembly location
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Muster Point Name *</Label>
              <Input
                value={newMusterPoint.name}
                onChange={(e) => setNewMusterPoint({ ...newMusterPoint, name: e.target.value })}
                placeholder="Main Parking Lot - Area A"
              />
            </div>
            
            <div>
              <Label>Location Description *</Label>
              <Textarea
                value={newMusterPoint.location}
                onChange={(e) => setNewMusterPoint({ ...newMusterPoint, location: e.target.value })}
                placeholder="North side of building, near main entrance"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Capacity (people)</Label>
              <Input
                type="number"
                value={newMusterPoint.capacity}
                onChange={(e) => setNewMusterPoint({ ...newMusterPoint, capacity: parseInt(e.target.value) || 50 })}
                placeholder="50"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <Label>Has First Aid Kit</Label>
                <Switch
                  checked={newMusterPoint.has_first_aid}
                  onCheckedChange={(checked) => setNewMusterPoint({ ...newMusterPoint, has_first_aid: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Has Communication</Label>
                <Switch
                  checked={newMusterPoint.has_communication}
                  onCheckedChange={(checked) => setNewMusterPoint({ ...newMusterPoint, has_communication: checked })}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMusterPoint(false)}>
              Cancel
            </Button>
            <Button onClick={addMusterPoint} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Muster Point
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyOperationsCenter;
